import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Key, Copy, Trash, Warning } from "@phosphor-icons/react";
import axios from "axios";
import config from "../config";

const API = `${config.BACKEND_URL}/api`;

export default function ApiKeysPage() {
  const { getAuthHeaders } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [showNewKey, setShowNewKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const res = await axios.get(`${API}/api-keys`, { headers: getAuthHeaders() });
      setApiKeys(res.data.api_keys || []);
    } catch (err) {
      console.error(err);
    }
  };

  const createKey = async () => {
    if (!newKeyName.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api-keys`,
        { name: newKeyName, permissions: ["read", "write"] },
        { headers: getAuthHeaders() }
      );
      setShowNewKey(res.data.api_key);
      setNewKeyName("");
      fetchApiKeys();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create API key");
    } finally {
      setLoading(false);
    }
  };

  const revokeKey = async (keyId) => {
    if (!confirm("Are you sure? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/api-keys/${keyId}`, { headers: getAuthHeaders() });
      fetchApiKeys();
    } catch (err) {
      alert("Failed to revoke API key");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">API Keys</h1>
        <p className="text-sm text-[#6B7280] mt-1">
          Manage API keys for programmatic access to your chatbots.
        </p>
      </div>

      <div className="border border-[#E5E7EB] p-6">
        <h2 className="font-heading text-lg font-bold mb-4">Create New API Key</h2>
        <div className="flex gap-3">
          <input
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="e.g., Production API Key"
            className="flex-1 border border-[#D1D5DB] px-4 py-2 text-sm outline-none focus:border-[#002FA7] transition-colors"
          />
          <Button
            onClick={createKey}
            disabled={loading || !newKeyName.trim()}
            className="rounded-none bg-[#002FA7] hover:bg-[#001F73] text-white text-sm h-10 px-6 transition-transform active:scale-[0.98] disabled:opacity-50"
          >
            {loading ? "Creating..." : "Create Key"}
          </Button>
        </div>
      </div>

      {showNewKey && (
        <div className="bg-amber-50 border border-amber-200 p-6">
          <div className="flex items-center gap-2 mb-3">
            {/* ✅ FIX: AlertTriangle → Warning (Phosphor Icons mein sahi naam) */}
            <Warning size={18} className="text-amber-600" />
            <h3 className="font-semibold text-amber-800 text-sm">
              Copy this API key now — it will never be shown again!
            </h3>
          </div>
          <div className="flex gap-2">
            <code className="flex-1 bg-white border border-amber-200 px-4 py-3 text-sm break-all font-mono">
              {showNewKey}
            </code>
            <Button
              onClick={() => copyToClipboard(showNewKey)}
              variant="outline"
              className="rounded-none border-amber-300 text-amber-700 hover:bg-amber-100"
            >
              <Copy size={16} />
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
          <Button
            onClick={() => setShowNewKey(null)}
            className="mt-3 rounded-none bg-amber-600 hover:bg-amber-700 text-white text-xs h-8"
          >
            I have copied it
          </Button>
        </div>
      )}

      <div>
        <h2 className="font-heading text-lg font-bold mb-4">Your API Keys</h2>
        {apiKeys.length === 0 ? (
          <div className="border border-dashed border-[#D1D5DB] p-12 text-center">
            <Key size={40} weight="thin" className="mx-auto text-[#D1D5DB] mb-4" />
            <p className="text-sm text-[#6B7280]">No API keys yet. Create one to get started.</p>
          </div>
        ) : (
          <div className="border border-[#E5E7EB]">
            {apiKeys.map((key, i) => (
              <div
                key={key.id}
                className={`flex items-center justify-between p-4 ${
                  i < apiKeys.length - 1 ? "border-b border-[#E5E7EB]" : ""
                }`}
              >
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{key.name}</div>
                  <div className="text-xs text-[#6B7280] mt-1">
                    Created: {new Date(key.created_at).toLocaleDateString()}
                    {key.last_used && ` · Last used: ${new Date(key.last_used).toLocaleDateString()}`}
                  </div>
                  <div className="text-xs text-[#002FA7] mt-1">
                    Permissions: {key.permissions?.join(", ") || "read, write"}
                  </div>
                </div>
                <Button
                  onClick={() => revokeKey(key.id)}
                  variant="outline"
                  className="rounded-none border-red-200 text-red-600 hover:bg-red-50 text-xs h-8 flex items-center gap-1"
                >
                  <Trash size={14} />
                  Revoke
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-6">
        <h2 className="font-heading text-lg font-bold mb-4">API Documentation</h2>
        <p className="text-sm text-[#6B7280] mb-4">
          Use your API key in the request header:
        </p>
        <code className="block bg-white border border-[#E5E7EB] px-4 py-3 text-sm font-mono mb-6">
          X-API-Key: cb_live_xxxxxxxxxxxxxxxx
        </code>

        <div className="space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A]/40 mb-2">
              List Websites
            </div>
            <code className="block bg-white border border-[#E5E7EB] px-4 py-2 text-xs font-mono">
              GET /api/public/websites
            </code>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A]/40 mb-2">
              Chat
            </div>
            <code className="block bg-white border border-[#E5E7EB] px-4 py-2 text-xs font-mono">
              POST /api/public/chat
            </code>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A]/40 mb-2">
              Chat History
            </div>
            <code className="block bg-white border border-[#E5E7EB] px-4 py-2 text-xs font-mono">
              GET /api/public/chat/WEBSITE_ID/history
            </code>
          </div>
        </div>
      </div>
    </div>
  );
}
